<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import moment from 'moment'
import { usePatrocinioStore } from './store/usePatrocinioStore'
import type { IPatrocinio } from './types'
import LayoutTable from '@/components/CDF/LayoutTable.vue'
import type { ITerm } from '@/components/CDF/SearchBar.vue'
import type { IHeader, ITableAction } from '@/pages/types/layoutTable.types'

withDefaults(defineProps<{
  isHeader: boolean
}>(), {
  isHeader: true,
})

definePage({
  meta: {
    action: 'list',
    subject: 'patrocinio',
  },
})

const store = usePatrocinioStore()

const {
  items,
  itemsPerPage,
  page,
  loading,
  searchTerm,
  isSearching,
  planos,
  fornecedores,
} = storeToRefs(store)

const {
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  destroy,
  onOrderBy,
  dialogDestroy,
  fetchPlano,
  fetchFornecedor,
} = store

const headers: ComputedRef<IHeader[]> = computed(() => {
  return [
    {
      title: 'Fornecedor',
      key: 'fornecedor.nome_fantasia',
    },
    {
      title: 'Plano',
      key: 'plano.titulo',
    },
    {
      title: 'Data Vencimento',
      key: 'vencimento',
    },
    {
      title: 'Status',
      key: 'status',
    },
  ]
})

const terms: ComputedRef<ITerm[]> = computed(() => {
  return [
    {
      title: 'Fornecedor',
      value: 'fornecedor_id',
      relationship: 'fornecedor',
      options: fornecedores.value,
      type: 'SELECT',
      itemValue: 'id',
      itemTitle: 'nome_fantasia',
    },
    {
      title: 'Plano',
      value: 'plano_id',
      relationship: 'plano',
      type: 'SELECT',
      options: planos.value,
      itemValue: 'id',
      itemTitle: 'titulo',
    },
    {
      title: 'Data Vencimento',
      value: 'vencimento',
    },
    {
      title: 'Status',
      value: 'status',
    },
  ] as ITerm[]
})

const actions: ITableAction[] = [
  {
    icon: 'tabler-edit',
    color: 'primary',
    can: {
      action: 'edit',
      subject: 'patrocinio',
    },
    to: (item: IPatrocinio) => `/admin/Patrocinio/editar/${item.id}`,
  },
  {
    icon: 'tabler-trash',
    color: 'error',
    can: {
      action: 'delete',
      subject: 'patrocinio',
    },
    onClick: (item: IPatrocinio) => dialogDestroy(item.id),
  },
]

onMounted(() => {
  fetchPlano()
  fetchFornecedor()
})

onBeforeRouteLeave(() => {
  store.$reset()
})
</script>

<template>
  <LayoutTable
    v-model:items="items"
    v-model:loading="loading.items"
    v-model:loading-destroy="loading.destroy"
    v-model:search="searchTerm"
    v-model:is-searching="isSearching"
    :destroy="destroy"
    :is-header="isHeader"
    title="Patrocinios"

    new-item="/admin/patrocinio/cadastrar"
    :headers="headers"
    :actions="actions"
    :items-per-page="itemsPerPage"
    :page="page"

    :load-more="loadMore"
    :terms-search="terms"
    :on-search="onSearch"
    :on-search-again="onSearchAgain"
    :on-reset="resetSearch"
    :on-order-by="onOrderBy"
  >
    <template #item.vencimento="{ item }">
      {{ moment(item.vencimento).format('DD/MM/YYYY') }}
    </template>
  </LayoutTable>
</template>
